import * as React from 'react'
import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslations } from 'next-intl'
import { toast } from 'sonner'
import { Loader2, UploadCloud } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useProductApi } from './api/product-api'
import { popModal } from '@/components/modals'
import type { Product } from './product-list'
import { ProjectMessage } from '@/lib/types'
import { nanoid } from 'nanoid'
import { useProjectPromptQueue } from '@/hooks/use-project-prompt-queue'
import { useAICoding } from '@/hooks/use-ai-coding'
import { useProjectStore } from '@/hooks/use-project-store'

const productSchema = z.object({
  name: z
    .string()
    .max(100, 'Product name must be 100 characters or less')
    .min(1, 'Product name is required'),
  description: z
    .string()
    .max(100, 'Description must be 100 characters or less')
    .optional(),
  price: z.number().min(0, 'Price must be positive'),
  currency: z.string().min(1, 'Currency is required'),
  type: z.enum(['digital', 'subscription']),
  status: z.enum(['active', 'inactive']),
  images: z.array(z.string()).optional(),
  // 统一商品配置
  productConfig: z
    .object({
      // 数字商品配置
      fileUrl: z.string().optional(), // 数字商品文件URL或在线地址
      // 订阅商品配置
      duration: z.number().min(1).optional(),
      unit: z.enum(['day', 'week', 'month', 'year']).optional(),
    })
    .optional(),
})

type ProductFormData = z.infer<typeof productSchema>

interface ShoppingProductFormModalProps {
  product?: Product
  productType?: 'digital' | 'subscription' // 新增：从类型选择弹窗传入的商品类型
  onSuccess?: () => void
  onClose?: () => void
}

export default function ShoppingProductFormModal({
  product,
  productType,
  onSuccess,
  onClose,
}: ShoppingProductFormModalProps) {
  const t = useTranslations('shopping-admin.products')
  const tUpload = useTranslations('shopping-admin.upload')
  const [loading, setLoading] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState<
    Array<{ id: string; file: File }>
  >([])
  const [digitalProductTab, setDigitalProductTab] = useState<'upload' | 'url'>(
    'upload',
  )
  const [tempOnlineUrl, setTempOnlineUrl] = useState('')
  const [urlError, setUrlError] = useState('')

  // URL校验函数
  const validateUrl = (url: string): boolean => {
    if (!url.trim()) return true // 空值允许

    try {
      const urlObj = new URL(url)
      // 检查协议是否为 http 或 https
      return urlObj.protocol === 'http:' || urlObj.protocol === 'https:'
    } catch {
      return false
    }
  }

  const { uploadFile, updateProduct } = useProductApi()
  const { addToPromptQueue } = useProjectPromptQueue()
  const { aiCoding } = useAICoding()
  const { aiStage, addMessage, api__appendMessage } = useProjectStore()

  const form = useForm<ProductFormData>({
    resolver: zodResolver(productSchema),
    defaultValues: {
      name: '',
      description: '',
      price: 1000,
      currency: 'usd',
      type: productType || 'digital', // 使用传入的 productType
      status: 'active',
      images: [],
      productConfig: {
        fileUrl: '',
        duration: 1,
        unit: 'month',
      },
    },
  })

  // 根据商品类型获取默认配置
  const getDefaultConfigForType = (type: 'digital' | 'subscription') => {
    if (type === 'digital') {
      return {
        fileUrl: '',
      }
    } else if (type === 'subscription') {
      return {
        duration: 1,
        unit: 'month' as 'day' | 'week' | 'month' | 'year',
      }
    }
    return {}
  }

  useEffect(() => {
    if (product) {
      form.reset({
        name: product.name,
        description: product.description,
        price: product.price / 100, // 转换为元
        currency: product.currency || 'usd',
        type: product.type,
        status: product.status,
        images: product.images || [],
        productConfig: {
          ...getDefaultConfigForType(product.type),
          ...product.productConfig,
        },
      })

      // 根据现有数据设置正确的tab和状态
      if (product.productConfig?.fileUrl) {
        if (product.productConfig.fileUrl.startsWith('http')) {
          setDigitalProductTab('url')
          setTempOnlineUrl(product.productConfig.fileUrl)
        } else {
          setDigitalProductTab('upload')
          setTempOnlineUrl('')
        }
      }
    }
  }, [product, form])

  // 监听商品类型变化，动态更新 productConfig（仅在创建新商品时）
  useEffect(() => {
    if (product) return // 编辑现有商品时不监听类型变化

    const subscription = form.watch((value, { name }) => {
      if (name === 'type' && value.type) {
        const currentConfig = form.getValues('productConfig') || {}
        const newConfig = getDefaultConfigForType(
          value.type as 'digital' | 'subscription',
        )

        // 保留当前类型相关的配置，清除不相关的配置
        const filteredConfig = { ...newConfig }
        if (value.type === 'digital' && currentConfig.fileUrl) {
          filteredConfig.fileUrl = currentConfig.fileUrl
        } else if (value.type === 'subscription') {
          if (currentConfig.duration)
            filteredConfig.duration = currentConfig.duration
          if (currentConfig.unit) filteredConfig.unit = currentConfig.unit
        }

        form.setValue('productConfig', filteredConfig)
      }
    })

    return () => subscription.unsubscribe()
  }, [form, product])

  const handleClose = () => {
    onClose?.()
    popModal()
  }

  // 图片上传处理函数
  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    const currentImages = form.watch('images') || []
    const maxImages = 5
    const remainingSlots = maxImages - currentImages.length

    if (remainingSlots <= 0) {
      toast.error(tUpload('max_images'))
      return
    }

    const filesToUpload = Array.from(files).slice(0, remainingSlots)

    if (files.length > remainingSlots) {
      toast.warning(
        tUpload('remaining_slots', {
          remainingSlots,
          ignoredCount: files.length - remainingSlots,
        }),
      )
    }

    setUploading(true)
    try {
      const newImages = [...(form.watch('images') || [])]

      for (let i = 0; i < filesToUpload.length; i++) {
        const file = filesToUpload[i]
        const formData = new FormData()
        formData.append('file', file)

        const { ossKey } = await uploadFile(formData)
        newImages.push(ossKey)
      }

      form.setValue('images', newImages)
    } catch (error) {
      console.error('Image upload error:', error)
      toast.error(tUpload('image_upload_failed'))
    } finally {
      setUploading(false)
    }
  }

  const removeImage = (index: number) => {
    const currentImages = form.watch('images') || []
    const newImages = currentImages.filter((_, i) => i !== index)
    form.setValue('images', newImages)
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    const fileArray = Array.from(files)

    // 累积添加文件到已选择的文件列表，为每个文件生成唯一ID
    const newFiles = fileArray.map((file) => ({
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}-${
        file.name
      }`,
      file,
    }))

    setSelectedFiles((prev) => {
      // 检查是否有重复的文件（基于文件名和大小）
      const existingFiles = prev.map(
        (item) => `${item.file.name}-${item.file.size}`,
      )
      const uniqueNewFiles = newFiles.filter(
        (item) =>
          !existingFiles.includes(`${item.file.name}-${item.file.size}`),
      )

      return [...prev, ...uniqueNewFiles]
    })

    // 清空input值，允许重复选择相同文件
    event.target.value = ''
  }

  const handleDigitalFileUpload = async () => {
    if (selectedFiles.length === 0) return

    try {
      const files = selectedFiles.map((item) => item.file)

      // 如果只有一个文件，直接上传
      if (files.length === 1) {
        const formData = new FormData()
        formData.append('file', files[0])

        const { ossKey } = await uploadFile(formData)
        form.setValue('productConfig.fileUrl', ossKey)
        toast.success(tUpload('file_upload_success'))
      } else {
        // 多个文件，打包成zip上传
        toast.info(tUpload('packing_files', { fileCount: files.length }))
        const zipBlob = await createZipFromFiles(files)
        const formData = new FormData()
        formData.append('file', zipBlob, 'digital-product.zip')

        const { ossKey } = await uploadFile(formData)
        form.setValue('productConfig.fileUrl', ossKey)
        toast.success(
          tUpload('zip_upload_success', { fileCount: files.length }),
        )
      }
    } catch (error) {
      console.error('Digital file upload error:', error)
      toast.error(tUpload('digital_file_upload_failed'))
    } finally {
      setSelectedFiles([])
    }
  }

  // 创建zip文件的函数
  const createZipFromFiles = async (files: File[]): Promise<Blob> => {
    // 动态导入JSZip
    const JSZip = (await import('jszip')).default
    const zip = new JSZip()

    // 将文件添加到zip中
    for (const file of files) {
      zip.file(file.name, file)
    }

    // 生成zip文件
    const zipBlob = await zip.generateAsync({ type: 'blob' })
    return zipBlob
  }
  const createProduct = async (data: ProductFormData) => {
    await createProductByAiCoding(data)
  }

  const createProductByAiCoding = async (data: ProductFormData) => {
    try {
      const contentForAi = buildAddProductPrompt(data)
      console.log('contentForAi', contentForAi)

      const userMessage: ProjectMessage = {
        id: nanoid(),
        role: 'user',
        content: t('form.actions.createProductMessage'),
        contentForAi,
      }

      if (aiStage === 'coding') {
        addToPromptQueue(userMessage)
      } else {
        const newUserMessage = await api__appendMessage(userMessage)
        if (!newUserMessage) {
          toast.error('Failed to create product message')
          return
        }
        addMessage(newUserMessage)
        aiCoding({ addedUserMessage: newUserMessage })
      }
    } catch (error) {
      console.error('Error creating product via AI:', error)
      toast.error('Failed to create product via AI')
    }
  }

  const onSubmit = async (data: ProductFormData) => {
    setLoading(true)
    try {
      // 如果是数字商品且有待上传的本地文件，则在提交前自动上传并写入 fileUrl
      if (
        data.type === 'digital' &&
        selectedFiles.length > 0 &&
        !form.getValues('productConfig')?.fileUrl
      ) {
        await handleDigitalFileUpload()
        // 等待状态更新后从表单中读取最新的 fileUrl
        data = {
          ...data,
          productConfig: {
            ...(data.productConfig || {}),
            fileUrl: form.getValues('productConfig')?.fileUrl || '',
          },
        }
      }

      // 根据商品类型构建相应的配置
      let productConfig: any = {}

      if (data.type === 'digital') {
        // 数字商品：根据当前tab决定使用哪个值
        let finalUrl = ''
        if (digitalProductTab === 'url' && tempOnlineUrl) {
          // 校验在线地址
          if (!validateUrl(tempOnlineUrl)) {
            toast.error(t('form.fields.invalidUrl'))
            return
          }
          // 当前是在线地址tab且有输入，使用在线地址
          finalUrl = tempOnlineUrl
        } else if (
          digitalProductTab === 'upload' &&
          data.productConfig?.fileUrl
        ) {
          // 当前是文件上传tab且有文件，使用文件URL
          finalUrl = data.productConfig.fileUrl
        }

        productConfig = {
          fileUrl: finalUrl,
        }
      } else if (data.type === 'subscription') {
        productConfig = {
          duration: data.productConfig?.duration || 'month',
          unit: data.productConfig?.unit || 1,
        }
      }

      const submitData: any = {
        name: data.name,
        description: data.description,
        price: data.price * 100,
        currency: data.currency,
        status: data.status,
        images: data.images,
        productConfig,
      }

      if (product) {
        // 更新商品时不传递 type 字段
        await updateProduct(product.id, submitData)
      } else {
        // 创建商品时需要传递 type 字段
        submitData.type = data.type
        await createProduct(submitData)
      }

      toast.success(
        product ? t('form.update.success') : t('form.create.success'),
      )
      onSuccess?.()
      handleClose()
    } catch (error) {
      console.error('Product save error:', error)
      toast.error(product ? t('form.update.error') : t('form.create.error'))
      setLoading(false)
    }
  }

  const isDigital = form.watch('type') === 'digital'
  const isSubscription = form.watch('type') === 'subscription'

  return (
    <Dialog open={true} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent
        className="max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden z-[80] p-0 rounded-[20px] border-gray-700 flex flex-col mx-2 sm:mx-4"
        style={{
          background:
            'linear-gradient(104deg, #222222 1.87%, #2B2B2B 56.52%, #010101 111.18%)',
        }}
        overlayClassName="z-[80]"
      >
        <DialogHeader className="px-4 sm:px-8 pt-4 sm:pt-6 pb-4">
          <DialogTitle className="text-lg sm:text-xl font-semibold text-white">
            {product ? t('form.edit.title') : t('form.create.title')}
          </DialogTitle>
        </DialogHeader>

        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex-1 flex flex-col min-h-0"
        >
          <div className="flex-1 overflow-y-auto space-y-4 sm:space-y-6 px-4 sm:px-8 pb-4 sm:pb-6 max-h-[calc(95vh-120px)] sm:max-h-[calc(90vh-120px)]">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div className="space-y-2">
                <Label
                  htmlFor="name"
                  className="text-sm font-medium text-white"
                >
                  {t('form.fields.name')}
                </Label>
                <div>
                  <Input
                    id="name"
                    {...form.register('name')}
                    placeholder={t('form.fields.namePlaceholder')}
                    className="h-11 text-sm bg-white/10 border-gray-600 text-white placeholder:text-gray-400 focus:border-white/30"
                    outerClassName="!relative !flex !size-full !items-center"
                  />
                </div>
                {form.formState.errors.name && (
                  <p className="text-sm text-red-500 mt-1">
                    {form.formState.errors.name.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="price"
                  className="text-sm font-medium text-white"
                >
                  {t('form.fields.price')}
                </Label>
                <div className="flex gap-2 h-11">
                  <Select
                    value={form.watch('currency')}
                    onValueChange={(value) => form.setValue('currency', value)}
                  >
                    <SelectTrigger className="w-20 h-11 text-sm bg-white/10 border-gray-600 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-gray-800 border-gray-600">
                      <SelectItem
                        value="usd"
                        className="text-white hover:bg-gray-700"
                      >
                        USD
                      </SelectItem>
                      <SelectItem
                        value="eur"
                        className="text-white hover:bg-gray-700"
                      >
                        EUR
                      </SelectItem>
                      <SelectItem
                        value="gbp"
                        className="text-white hover:bg-gray-700"
                      >
                        GBP
                      </SelectItem>
                      <SelectItem
                        value="cny"
                        className="text-white hover:bg-gray-700"
                      >
                        CNY
                      </SelectItem>
                      <SelectItem
                        value="rub"
                        className="text-white hover:bg-gray-700"
                      >
                        RUB
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    {...form.register('price', { valueAsNumber: true })}
                    placeholder="0.00"
                    className="h-11 text-sm flex-1 bg-white/10 border-gray-600 text-white placeholder:text-gray-400 focus:border-white/30"
                    outerClassName="!relative !flex !size-full !items-center"
                  />
                </div>
                {form.formState.errors.price && (
                  <p className="text-sm text-red-500 mt-1">
                    {form.formState.errors.price.message}
                  </p>
                )}
                {form.formState.errors.currency && (
                  <p className="text-sm text-red-500 mt-1">
                    {form.formState.errors.currency.message}
                  </p>
                )}
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label
                  htmlFor="description"
                  className="text-sm font-medium text-white"
                >
                  {t('form.fields.description')}
                </Label>
                <span className="text-xs text-white/60">
                  {form.watch('description')?.length || 0}/100
                </span>
              </div>
              <Textarea
                id="description"
                {...form.register('description')}
                placeholder={t('form.fields.descriptionPlaceholder')}
                rows={3}
                maxLength={100}
                className="text-sm resize-none bg-white/10 border-gray-600 text-white placeholder:text-gray-400 focus:border-white/30"
              />
              {form.formState.errors.description && (
                <p className="text-sm text-red-500 mt-1">
                  {form.formState.errors.description.message}
                </p>
              )}
            </div>

            {/* 图片上传区域 */}
            <div className="space-y-4">
              <Label
                htmlFor="images"
                className="text-sm font-medium text-white"
              >
                {t('form.fields.images')}
              </Label>
              <div className="space-y-4">
                {/* 上传按钮和已上传图片在同一行 */}
                <div className="flex items-center gap-1">
                  {/* 上传按钮 */}
                  <div>
                    <Label
                      htmlFor="images-upload"
                      className={`flex flex-col items-center justify-center w-20 h-20 rounded-xl cursor-pointer transition-all flex-shrink-0 ${
                        (form.watch('images') || []).length >= 5
                          ? 'border border-white/40 bg-white/5 text-gray-400 cursor-not-allowed'
                          : 'border border-white/40 hover:border-white/60 hover:bg-white/10 text-white'
                      }`}
                      style={{
                        background: '#FFFFFF0A',
                        border: '1px dashed #FFFFFF66',
                        borderStyle: 'dashed',
                        borderImage: 'none',
                      }}
                    >
                      {uploading ? (
                        <Loader2 className="h-4 w-4 animate-spin mb-1" />
                      ) : (
                        <UploadCloud className="h-4 w-4 mb-1" />
                      )}
                    </Label>
                    <Input
                      id="images-upload"
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageUpload}
                      className="hidden"
                      disabled={(form.watch('images') || []).length >= 5}
                    />
                  </div>
                  {/* 已上传的图片预览 - 紧挨着上传按钮右边，从左往右依次排列 */}
                  {(form.watch('images') || []).map((image, index) => (
                    <div key={index} className="relative group flex-shrink-0">
                      <div className="h-20 w-20 rounded-xl overflow-hidden border border-white/40 cursor-pointer hover:opacity-80 transition-opacity shadow-sm">
                        <img
                          src={`${
                            image.startsWith('http')
                              ? image
                              : `https://pub-fcdf2a6833174e68902966cf14dc111d.r2.dev/${image}`
                          }`}
                          alt={`Product ${index + 1}`}
                          className="object-cover w-full h-full"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={() => removeImage(index)}
                        className="absolute -top-1 -right-1 bg-white text-black rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-gray-100 opacity-0 group-hover:opacity-100 transition-opacity shadow-lg"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
                <div className="text-sm text-gray-400">
                  {t('form.fields.imagesUploaded', {
                    count: (form.watch('images') || []).length,
                    max: 5,
                  })}
                </div>
              </div>
              {form.formState.errors.images && (
                <p className="text-sm text-red-500">
                  {form.formState.errors.images.message}
                </p>
              )}
            </div>
            <div className="space-y-3">
              <Label
                htmlFor="status"
                className="text-sm font-medium text-white"
              >
                {t('form.fields.status')}
              </Label>
              <Select
                value={form.watch('status')}
                onValueChange={(value) =>
                  form.setValue('status', value as 'active' | 'inactive')
                }
                onOpenChange={(open) =>
                  console.log('Status select open:', open)
                }
              >
                <SelectTrigger className="w-full h-11 text-sm bg-white/10 border-gray-600 text-white">
                  <SelectValue placeholder="选择状态" />
                </SelectTrigger>
                <SelectContent className="bg-gray-800 border-gray-600">
                  <SelectItem
                    value="active"
                    className="text-white hover:bg-gray-700"
                  >
                    {t('status.active')}
                  </SelectItem>
                  <SelectItem
                    value="inactive"
                    className="text-white hover:bg-gray-700"
                  >
                    {t('status.inactive')}
                  </SelectItem>
                </SelectContent>
              </Select>
              {form.formState.errors.status && (
                <p className="text-sm text-red-500 mt-1">
                  {form.formState.errors.status.message}
                </p>
              )}
            </div>

            {/* 数字商品配置区域 */}
            {isDigital && (
              <div className="space-y-6 border-2 border-gray-200 dark:border-gray-700 rounded-xl p-6 bg-gray-50 dark:bg-gray-800/50">
                {/* Tab切换 */}
                <div className="flex space-x-1 bg-gray-200 dark:bg-gray-700 p-1 rounded-lg">
                  <button
                    type="button"
                    onClick={() => {
                      setDigitalProductTab('upload')
                      // 切换到文件上传时，不清空任何内容，让用户可以自由切换
                      // 保持所有现有状态
                    }}
                    className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                      digitalProductTab === 'upload'
                        ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    {t('form.fields.uploadFile')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setDigitalProductTab('url')
                      // 切换到在线地址时，只清空已选择的文件（未上传的）
                      setSelectedFiles([])
                      // 不清空已上传的文件URL，让用户可以切换回文件上传tab查看
                      // 如果当前是在线地址，保持不变
                    }}
                    className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                      digitalProductTab === 'url'
                        ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-gray-100 shadow-sm'
                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                    }`}
                  >
                    {t('form.fields.onlineUrl')}
                  </button>
                </div>

                {/* Tab内容 */}
                <div className="space-y-4">
                  {/* 文件上传Tab */}
                  {digitalProductTab === 'upload' && (
                    <div className="space-y-4">
                      {/* 已上传的文件 */}
                      {form.watch('productConfig.fileUrl') &&
                        !form
                          .watch('productConfig.fileUrl')
                          ?.startsWith('http') && (
                          <div className="space-y-3">
                            <div className="flex items-center gap-3 p-4 border-2 border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 shadow-sm">
                              <div className="text-sm flex-1">
                                <div className="font-semibold text-gray-900 dark:text-gray-100">
                                  {t('form.fields.digitalProductFile')}
                                </div>
                                <div className="text-gray-500 dark:text-gray-400 mt-1 break-all">
                                  {form
                                    .watch('productConfig.fileUrl')
                                    ?.split('/')
                                    .pop()}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  form.setValue('productConfig.fileUrl', '')
                                }}
                                className="text-red-500 hover:text-red-700 p-2 rounded-full hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                              >
                                ×
                              </button>
                            </div>
                          </div>
                        )}

                      {/* 文件选择按钮 */}
                      <Label
                        htmlFor="digital-file-upload"
                        className="flex items-center justify-center w-full h-12 px-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:border-gray-400 hover:bg-gray-50 dark:hover:border-gray-500 dark:hover:bg-gray-800 transition-all"
                      >
                        <UploadCloud className="h-4 w-4 mr-2" />
                        <span className="text-sm font-medium">
                          {selectedFiles.length > 0
                            ? t('form.fields.filesSelected', {
                                count: selectedFiles.length,
                              })
                            : form.watch('productConfig.fileUrl') &&
                                !form
                                  .watch('productConfig.fileUrl')
                                  ?.startsWith('http')
                              ? t('form.fields.replaceDigitalFile')
                              : t('form.fields.selectDigitalFile')}
                        </span>
                      </Label>
                      <Input
                        id="digital-file-upload"
                        type="file"
                        accept="*"
                        multiple
                        onChange={handleFileSelect}
                        className="hidden"
                      />

                      {/* 显示选中的文件信息 */}
                      {selectedFiles.length > 0 && (
                        <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                          <div className="text-sm text-blue-800 dark:text-blue-200">
                            <div className="flex items-center gap-2 flex-wrap">
                              {selectedFiles.map((fileItem) => (
                                <div
                                  key={fileItem.id}
                                  className="relative group flex-shrink-0"
                                >
                                  <div className="w-20 h-20 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg flex flex-col items-center justify-center text-xs p-1">
                                    <div className="text-center truncate w-full text-gray-900 dark:text-gray-100">
                                      {fileItem.file.name}
                                    </div>
                                    <div className="text-gray-500 dark:text-gray-400 text-xs mt-1">
                                      {(fileItem.file.size / 1024).toFixed(1)}{' '}
                                      KB
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setSelectedFiles((prev) =>
                                        prev.filter(
                                          (item) => item.id !== fileItem.id,
                                        ),
                                      )
                                    }}
                                    className="absolute -top-1 -right-1 w-5 h-5 bg-white text-black rounded-full flex items-center justify-center text-xs hover:bg-gray-100 border border-gray-300"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* 在线地址Tab */}
                  {digitalProductTab === 'url' && (
                    <div className="space-y-4">
                      <div className="space-y-3">
                        <Label
                          htmlFor="fileUrl"
                          className="text-sm font-medium text-gray-700 dark:text-gray-300"
                        >
                          {t('form.fields.digitalProductUrl')}
                        </Label>
                        <Input
                          id="onlineUrl"
                          value={tempOnlineUrl}
                          onChange={(e) => {
                            const value = e.target.value
                            setTempOnlineUrl(value)

                            // 实时校验URL
                            if (value.trim() && !validateUrl(value)) {
                              setUrlError(t('form.fields.invalidUrl'))
                            } else {
                              setUrlError('')
                            }
                          }}
                          placeholder={t(
                            'form.fields.digitalProductUrlPlaceholder',
                          )}
                          className={`h-11 text-sm ${urlError ? 'border-red-500 focus:border-red-500' : ''}`}
                        />
                        {urlError && (
                          <div className="text-sm text-red-500 mt-1">
                            {urlError}
                          </div>
                        )}
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          {t('form.fields.digitalProductUrlDescription')}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* 订阅商品配置区域 */}
            {isSubscription && (
              <div className="space-y-6 border-2 border-gray-200 dark:border-gray-700 rounded-xl p-6 bg-gray-50 dark:bg-gray-800/50">
                <div className="grid grid-cols-2 gap-8">
                  <div className="space-y-3">
                    <Label
                      htmlFor="subscriptionUnit"
                      className="text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      {t('form.fields.subscriptionDuration')}
                    </Label>
                    <div>
                      <Input
                        id="subscriptionDuration"
                        type="number"
                        min="1"
                        {...form.register('productConfig.duration', {
                          valueAsNumber: true,
                        })}
                        placeholder="1"
                        className="h-11 text-sm"
                      />
                    </div>
                    {form.formState.errors.productConfig?.duration && (
                      <p className="text-sm text-red-500 mt-1">
                        {form.formState.errors.productConfig.duration.message}
                      </p>
                    )}
                  </div>

                  <div className="space-y-3">
                    <Label
                      htmlFor="duration"
                      className="text-sm font-medium text-gray-700 dark:text-gray-300"
                    >
                      {t('form.fields.subscriptionUnit')}
                    </Label>
                    <Select
                      value={form.watch('productConfig.unit')}
                      onValueChange={(value) =>
                        form.setValue(
                          'productConfig.unit',
                          value as 'day' | 'week' | 'month' | 'year',
                        )
                      }
                    >
                      <SelectTrigger className="w-full h-11 text-sm">
                        <SelectValue
                          placeholder={t(
                            'form.fields.subscriptionUnitPlaceholder',
                          )}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="day">
                          {t('form.fields.subscriptionUnits.day')}
                        </SelectItem>
                        <SelectItem value="week">
                          {t('form.fields.subscriptionUnits.week')}
                        </SelectItem>
                        <SelectItem value="month">
                          {t('form.fields.subscriptionUnits.month')}
                        </SelectItem>
                        <SelectItem value="year">
                          {t('form.fields.subscriptionUnits.year')}
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    {form.formState.errors.productConfig?.unit && (
                      <p className="text-sm text-red-500 mt-1">
                        {form.formState.errors.productConfig.unit.message}
                      </p>
                    )}
                  </div>
                </div>

                {/* 订阅周期预览 */}
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <div className="text-sm text-blue-800 dark:text-blue-200">
                    <span className="font-medium">
                      {t('form.fields.subscriptionPreview')}
                    </span>
                    {form.watch('productConfig.unit') &&
                      form.watch('productConfig.duration') && (
                        <span className="ml-2">
                          {t('form.fields.subscriptionPreviewText', {
                            duration: form.watch('productConfig.duration') || 1,
                            unit: t(
                              `form.fields.subscriptionUnits.${form.watch(
                                'productConfig.unit',
                              )}`,
                            ),
                          })}
                        </span>
                      )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="pt-4 sm:pt-6 flex-shrink-0 px-4 sm:px-8 pb-4 sm:pb-6">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 w-full">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="w-full sm:flex-1 h-11 text-sm font-medium bg-gray-700 border-gray-600 text-white hover:bg-gray-600"
                disabled={loading || uploading}
              >
                {t('form.actions.cancel')}
              </Button>
              <Button
                type="submit"
                className="w-full sm:flex-1 h-11 text-sm font-medium bg-white text-black hover:bg-gray-100"
                loading={loading}
                disabled={
                  uploading ||
                  (digitalProductTab === 'url' && !validateUrl(tempOnlineUrl))
                }
              >
                {product ? t('form.actions.update') : t('form.actions.create')}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function buildAddProductPrompt(data: any) {
  const {
    name = '',
    description = '',
    price = 0,
    currency = 'usd',
    status = 'active',
    images = [],
    productConfig = {},
    type = 'digital',
  } = data

  const getProductTypeInstructions = () => {
    if (type === 'digital') {
      return `
- This is a digital product with downloadable files
- File URL: ${productConfig?.fileUrl || 'Not provided'}
- Users will receive a download link after purchase`
    } else if (type === 'subscription') {
      const duration = productConfig?.duration || 1
      const unit = productConfig?.unit || 'month'
      return `
- This is a subscription product with duration: ${duration} ${unit}
- Users will receive subscription service for the specified period`
    }
    return '- Product type configuration not provided'
  }

  const getImagesInfo = () => {
    if (!images || images.length === 0) {
      return '- No images'
    }
    return images
      .map(
        (imageUrl: string, index: number) =>
          `- Image ${index + 1}: ${imageUrl}`,
      )
      .join('\n')
  }

  return `
You are a professional e-commerce product creation assistant. Please help the user refine and optimize this product based on the following information:

## Basic Product Information
- **Product Name**: ${name}
- **Description**: ${description}
- **Currency**: ${currency}
- **Price (in cents)**: ${price}
- **Status**: ${status === 'active' ? 'Active' : 'Inactive'}
- **Product Type**: ${type === 'digital' ? 'Digital Product' : 'Subscription Product'}

## Product Images
${getImagesInfo()}

## Product Configuration
${getProductTypeInstructions()}

Please call the /api/admin-products endpoint to create this product.
  `
}
